import { useState, useMemo, useCallback, useRef } from 'react';
import { useToast } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { useQuery } from 'hooks/useCustomQuery';
import FormBase from '@/components/forms/new/FormBase';
import {
    Box,
    Stack,
    Text,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Input,
    Tooltip,
    FormControl,
    FormLabel,
} from '@chakra-ui/react';
import {
    stackSpacing,
    formLabelStyling,
    stackStyling,
    descriptionStyling,
} from '@/components/forms/new/formStyles';
import PlanNameField from './formFields/PlanNameField';
import PlanDescriptionField from './formFields/PlanDescriptionField';
import PlanWholesalerField from './formFields/PlanWholesalerField';
import PlanPartnerField from './formFields/PlanPartnerField';
import PlanInternationalRateListField from './formFields/PlanInternationalRateListField';
import PlanInternationalDiscountField from './formFields/PlanInternationalDiscountField';
import PlanCountryField from './formFields/PlanCountryField';
import PlanPrefixField from './formFields/PlanPrefixField';
import PlanCompanySetupFeeField from './formFields/PlanCompanySetupFeeField';
import PlanSimplePortingFeeField from './formFields/PlanSimplePortingFeeField';
import PlanComplexPortingFeeField from './formFields/PlanComplexPortingFeeField';
import PlanOtherPortingFeeField from './formFields/PlanOtherPortingFeeField';
import { Field } from 'formik';
import BooleanToggle from '@/components/forms/new/formikFormFields/BooleanToggle';
import { axios } from '../../../../services/axios';
import walkthroughIds from '../walkthroughIds';

// API helpers
const fetchInternationalRateLists = async (partnerID, wholesalerID) => {
    if (!partnerID && !wholesalerID) {
        return axios.get('/internationalratelists/', {
            params: { globalOnly: true },
        });
    }
    if (!partnerID && wholesalerID) {
        return axios.get(`/internationalratelists/wholesaler/${wholesalerID}`, {
            params: { withPublic: true },
        });
    }
    if (partnerID && wholesalerID) {
        return axios.get(`/internationalratelists/partner/${partnerID}`, {
            params: { withPublic: true },
        });
    }
    return { data: [] };
};

// Initial values for FormBase
const initialValues = {
    name: '',
    isActive: true,
    description: '',
    wholesalerID: '',
    partnerID: '',
    internationalRateListID: '',
    internationalDiscount: '',
    internationalType: true,
    country: '',
    countryPrefix: '',
    companySetupFee: '',
    simplePortingFee: '',
    complexPortingFee: '',
    otherPortingFee: '',
    userSetupFee: '',
    trunkSetupFee: '',
    sipChannelSetup: '',
    rgsSetupFee: '',
    userRate: '',
    trunkRate: '',
    sipChannelRate: '',
    rgsRate: '',
    userType: true,
    trunkType: true,
    rgsType: true,
};

// No Yup validation. Required validation for name, country, and prefix is handled in their respective formFields components, matching Approval Codes style.

// Final submit handler

// Final submit handler with toast notifications
const finalFormSubmit = async (
    fields,
    actions,
    isEdit,
    planId,
    toast,
    requiredScope,
    currentWholesaler,
    currentPartner,
) => {
    try {
        // Remove empty or null fields before sending
        const filteredFields = Object.fromEntries(
            Object.entries(fields).filter(
                ([, value]) =>
                    value !== '' && value !== null && value !== undefined,
            ),
        );

        // For wholesaler admin (60), ensure wholesalerID is included
        if (requiredScope === 60 && currentWholesaler) {
            filteredFields.wholesalerID = currentWholesaler;
        }

        // For partner admin (40), ensure both wholesalerID and partnerID are included
        if (requiredScope === 40 && currentWholesaler && currentPartner) {
            filteredFields.wholesalerID = currentWholesaler;
            filteredFields.partnerID = currentPartner;
        }

        if (isEdit && planId) {
            let response = await axios.put(
                '/plan',
                { ...filteredFields, id: planId },
                { headers: { 'X-RequestScope': requiredScope } },
            );
            toast({
                title: 'Plan updated',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            if (response.status === 200) {
                window.location.reload();
            }
            return true;
        } else {
            let response = await axios.post('/plan/', filteredFields, {
                headers: { 'X-RequestScope': requiredScope },
            });
            toast({
                title: 'Plan created',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            if (response.status === 200) {
                window.location.reload();
            }
            return true;
        }
    } catch (error) {
        //actions.setSubmitting(false);

        // Debug: log the values being set after API error
        console.log(
            'Resetting form to submitted values after API error:',
            fields,
        );
        if (typeof actions.setValues === 'function' && fields) {
            actions.setValues(fields);
        }
        if (typeof actions.setSubmitting === 'function') {
            actions.setSubmitting(false);
        }

        toast({
            title: 'Error',
            description:
                error?.response?.data?.message ||
                error.message ||
                'Something went wrong',
            status: 'error',
            duration: 4000,
            isClosable: true,
        });
        return false;
    }
};

const PlanDetails = ({ initialData = {}, isEdit = false, setPlansAction }) => {
    const toast = useToast();
    const plans = walkthroughIds?.plans || {};
    const { requiredScope } = useSelector((state) => state.login);
    const { currentWholesaler, currentPartner } = useSelector(
        (state) => state.navigationLists,
    );
    // Wholesaler/Partner dropdown state
    const [wholesalerOptions, setWholesalerOptions] = useState([]);
    const [partnerOptions, setPartnerOptions] = useState([]);
    const [enumArray, setEnumArray] = useState([]);
    const submittingFormRef = useRef(false);

    const [selectedWholesalerID, setSelectedWholesalerID] = useState(
        initialData.wholesalerID ||
            ([40, 60, 80].includes(requiredScope) ? currentWholesaler : '') ||
            '',
    );
    const [selectedPartnerID, setSelectedPartnerID] = useState(
        initialData.partnerID ||
            ([40, 80].includes(requiredScope) ? currentPartner : '') ||
            '',
    );
    const { darkMode } = useSelector((state) => state.settings);

    const { isLoading: loadingWholesalers } = useQuery({
        queryKey: ['planWholesalers', requiredScope],
        queryFn: async () => {
            const res = await axios.get('/wholesalers/');
            return res.data || [];
        },
        enabled: requiredScope === 80 || requiredScope === 60,
        onSuccess: (data) => setWholesalerOptions(data),
    });

    const { isLoading: loadingPartners } = useQuery({
        queryKey: ['planPartners', requiredScope, selectedWholesalerID],
        queryFn: async () => {
            const res = await axios.get(`/partners/${selectedWholesalerID}`, {
                headers: { 'X-RequestScope': requiredScope },
            });
            return res.data || [];
        },
        enabled:
            requiredScope >= 60 &&
            Boolean(selectedWholesalerID && selectedWholesalerID !== ''),
        onSuccess: (data) => setPartnerOptions(data),
    });

    useQuery({
        queryKey: [
            'planInternationalRateLists',
            selectedPartnerID,
            selectedWholesalerID,
        ],
        queryFn: () =>
            fetchInternationalRateLists(
                selectedPartnerID,
                selectedWholesalerID,
            ),
        onSuccess: (res) => setEnumArray(res.data),
    });

    const handleFinalFormSubmit = useCallback(
        async (fields, actions) => {
            if (submittingFormRef.current) {
                return;
            }

            submittingFormRef.current = true;
            const didSubmit = await finalFormSubmit(
                fields,
                actions,
                isEdit,
                initialData.id,
                toast,
                requiredScope,
                currentWholesaler,
                currentPartner,
            );

            if (!didSubmit) {
                submittingFormRef.current = false;
            }
        },
        [
            currentPartner,
            currentWholesaler,
            initialData.id,
            isEdit,
            requiredScope,
            toast,
        ],
    );

    // Memoize initial values to prevent Formik from resetting on every render
    // For wholesaler users, pre-fill wholesalerID with their own wholesaler
    // For partner users, pre-fill both wholesalerID and partnerID
    // For global users, pre-fill both from the currently managed context, if any
    // initialData's wholesalerID/partnerID are only applied when non-empty so
    // callers passing blank placeholders (e.g. the Add Plan form) don't clobber these defaults
    const memoizedInitialValues = useMemo(() => {
        const contextDefaults =
            !isEdit &&
            (requiredScope === 60 ||
                requiredScope === 40 ||
                requiredScope === 80)
                ? {
                      ...(currentWholesaler
                          ? { wholesalerID: currentWholesaler }
                          : {}),
                      ...(requiredScope !== 60 && currentPartner
                          ? { partnerID: currentPartner }
                          : {}),
                  }
                : {};
        const cleanedInitialData = Object.fromEntries(
            Object.entries(initialData).filter(
                ([key, value]) =>
                    !(
                        (key === 'wholesalerID' || key === 'partnerID') &&
                        (value === '' || value === null || value === undefined)
                    ),
            ),
        );

        return {
            ...initialValues,
            ...contextDefaults,
            ...cleanedInitialData,
        };
    }, [initialData, requiredScope, currentWholesaler, currentPartner, isEdit]);

    return (
        <FormBase
            // header={isEdit ? 'Edit Plan' : 'Create Plan'}
            initialValues={memoizedInitialValues}
            enableReinitialize
            // validationSchema={validationSchema}
            customClick
            finalFormSubmit={handleFinalFormSubmit}
            onBack={() => {
                setPlansAction('Get');
            }}>
            {({ values, errors, touched, setFieldValue }) => (
                <Stack spacing={6}>
                    <Box>
                        <Text fontWeight={500} fontSize="16px" mt={4} mb={2}>
                            Name &amp; Description
                        </Text>
                        <PlanNameField
                            errors={errors}
                            touched={touched}
                            darkMode={false}
                        />
                        <FormControl sx={{ margin: '0 !important' }}>
                            <Stack
                                direction="row"
                                spacing={stackSpacing}
                                sx={stackStyling()}
                                alignItems="center">
                                <FormLabel
                                    sx={{
                                        ...formLabelStyling(darkMode),
                                        paddingTop: '15px',
                                    }}>
                                    Active
                                </FormLabel>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        width: '100%',
                                        alignItems: 'center',
                                        paddingTop: '15px',
                                    }}>
                                    <Field
                                        component={BooleanToggle}
                                        id="isActive"
                                        name="isActive"
                                        isChecked={values.isActive}
                                        onChange={(value) =>
                                            setFieldValue('isActive', value)
                                        }
                                        walkthroughId={plans.ufActiveSwitch}
                                    />
                                </Box>
                            </Stack>
                        </FormControl>
                        <PlanDescriptionField
                            errors={errors}
                            touched={touched}
                            darkMode={false}
                        />
                    </Box>

                    {requiredScope > 40 && (
                        <Box>
                            <Text
                                fontWeight={500}
                                fontSize="16px"
                                mt={4}
                                mb={2}>
                                Belongs To
                            </Text>
                            {/* Only show wholesaler field for Global users (80) */}
                            {requiredScope === 80 && (
                                <PlanWholesalerField
                                    errors={errors}
                                    touched={touched}
                                    values={values}
                                    setFieldValue={setFieldValue}
                                    darkMode={darkMode}
                                    loadingWholesalers={loadingWholesalers}
                                    wholesalerOptions={wholesalerOptions}
                                    setSelectedWholesalerID={
                                        setSelectedWholesalerID
                                    }
                                    setSelectedPartnerID={setSelectedPartnerID}
                                    setPartnerOptions={setPartnerOptions}
                                />
                            )}
                            {/* Show wholesaler name as read-only text for Wholesaler users (60) */}
                            {requiredScope === 60 && (
                                <FormControl sx={{ margin: '0 !important' }}>
                                    <Stack
                                        direction="row"
                                        spacing={stackSpacing}
                                        sx={stackStyling()}>
                                        <Box>
                                            <FormLabel
                                                sx={formLabelStyling(darkMode)}>
                                                Wholesaler
                                            </FormLabel>
                                            <Text
                                                sx={descriptionStyling}
                                                maxWidth={250}>
                                                Your assigned wholesaler for
                                                this plan.
                                            </Text>
                                        </Box>
                                        <Box
                                            flex="1"
                                            sx={{ paddingTop: '15px' }}>
                                            <Text fontSize="16px">
                                                {wholesalerOptions.find(
                                                    (w) =>
                                                        w.id ===
                                                        currentWholesaler,
                                                )?.name || 'Loading...'}
                                            </Text>
                                        </Box>
                                    </Stack>
                                </FormControl>
                            )}
                            {/* Show partner field for Global (80) and Wholesaler (60) users */}
                            <PlanPartnerField
                                errors={errors}
                                touched={touched}
                                values={values}
                                setFieldValue={setFieldValue}
                                darkMode={darkMode}
                                loadingPartners={loadingPartners}
                                partnerOptions={partnerOptions}
                                setSelectedPartnerID={setSelectedPartnerID}
                                currentWholesaler={currentWholesaler}
                            />
                        </Box>
                    )}

                    <Box>
                        <Text fontWeight={500} fontSize="16px" mt={4} mb={2}>
                            International Rates
                        </Text>
                        <PlanInternationalRateListField
                            errors={errors}
                            touched={touched}
                            values={values}
                            setFieldValue={setFieldValue}
                            darkMode={darkMode}
                            enumArray={enumArray}
                        />
                        <PlanInternationalDiscountField
                            errors={errors}
                            touched={touched}
                            darkMode={false}
                        />
                        {/* Bill field (Formik Field + BooleanToggle) */}
                        <FormControl sx={{ margin: '0 !important' }}>
                            <Stack
                                direction="row"
                                spacing={stackSpacing}
                                sx={stackStyling()}
                                alignItems="center">
                                <FormLabel
                                    sx={{
                                        ...formLabelStyling(darkMode),
                                        paddingTop: '15px',
                                    }}>
                                    Bill
                                </FormLabel>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        width: '100%',
                                        alignItems: 'center',
                                        paddingTop: '15px',
                                    }}>
                                    <Field
                                        component={BooleanToggle}
                                        id="internationalType"
                                        name="internationalType"
                                        isChecked={values.internationalType}
                                        onChange={(value) =>
                                            setFieldValue(
                                                'internationalType',
                                                value,
                                            )
                                        }
                                        walkthroughId={plans.ufBillSwitch}
                                    />
                                </Box>
                            </Stack>
                        </FormControl>
                    </Box>

                    <Box>
                        <Text fontWeight={500} fontSize="16px" mt={4} mb={2}>
                            Configurations
                        </Text>
                        {/* Country field */}
                        <PlanCountryField
                            errors={errors}
                            touched={touched}
                            darkMode={false}
                        />
                        {/* Prefix field */}
                        <PlanPrefixField
                            errors={errors}
                            touched={touched}
                            darkMode={false}
                        />
                        {/* Company Setup Fee field */}
                        <PlanCompanySetupFeeField
                            errors={errors}
                            touched={touched}
                            darkMode={false}
                        />
                        {/* Simple Porting Fee field */}
                        <PlanSimplePortingFeeField
                            errors={errors}
                            touched={touched}
                            darkMode={false}
                        />
                        {/* Complex Porting Fee field */}
                        <PlanComplexPortingFeeField
                            errors={errors}
                            touched={touched}
                            darkMode={false}
                        />
                        {/* Other Porting Fee field */}
                        <PlanOtherPortingFeeField
                            errors={errors}
                            touched={touched}
                            darkMode={false}
                        />
                    </Box>

                    <Box>
                        <Text fontWeight={500} fontSize="16px" mt={4} mb={2}>
                            Attributes
                        </Text>
                        <Table variant="simple" size="sm">
                            <Thead>
                                <Tr>
                                    <Th textTransform="none"></Th>
                                    <Th textTransform="none">User</Th>
                                    <Th textTransform="none">Trunk</Th>
                                    <Th textTransform="none">Channels</Th>
                                    <Th textTransform="none">
                                        Resource Account
                                    </Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {/* Setup Fee Row */}
                                <Tr>
                                    <Td fontWeight="bold">Setup Fee</Td>
                                    <Td>
                                        <Tooltip
                                            label="User Setup Fee"
                                            placement="top"
                                            hasArrow>
                                            <Input
                                                style={{
                                                    background: darkMode
                                                        ? '#181A20'
                                                        : 'white',
                                                    color: `${darkMode ? 'dark.textDark' : 'inherit'}`,
                                                    borderColor: `${darkMode ? 'dark.tcap-borderGray' : 'inherit'}`,
                                                }}
                                                type="number"
                                                step="any"
                                                value={values?.userSetupFee}
                                                onChange={(e) =>
                                                    setFieldValue(
                                                        'userSetupFee',
                                                        e.target.value,
                                                    )
                                                }
                                                data-walkthroughid={
                                                    plans?.ufSetupFeeUserTd
                                                }
                                            />
                                        </Tooltip>
                                    </Td>
                                    <Td>
                                        <Tooltip
                                            label="Trunk Setup Fee"
                                            placement="top"
                                            hasArrow>
                                            <Input
                                                style={{
                                                    background: darkMode
                                                        ? '#181A20'
                                                        : 'white',
                                                    color: `${darkMode ? 'dark.textDark' : 'inherit'}`,
                                                    borderColor: `${darkMode ? 'dark.tcap-borderGray' : 'inherit'}`,
                                                }}
                                                type="number"
                                                step="any"
                                                value={values?.trunkSetupFee}
                                                onChange={(e) =>
                                                    setFieldValue(
                                                        'trunkSetupFee',
                                                        e.target.value,
                                                    )
                                                }
                                                data-walkthroughid={
                                                    plans.ufSetupFeeTrunkTd
                                                }
                                            />
                                        </Tooltip>
                                    </Td>
                                    <Td>
                                        <Tooltip
                                            label="Channels Setup Fee"
                                            placement="top"
                                            hasArrow>
                                            <Input
                                                style={{
                                                    background: darkMode
                                                        ? '#181A20'
                                                        : 'white',
                                                    color: `${darkMode ? 'dark.textDark' : 'inherit'}`,
                                                    borderColor: `${darkMode ? 'dark.tcap-borderGray' : 'inherit'}`,
                                                }}
                                                type="number"
                                                step="any"
                                                value={values?.sipChannelSetup}
                                                onChange={(e) =>
                                                    setFieldValue(
                                                        'sipChannelSetup',
                                                        e.target.value,
                                                    )
                                                }
                                                data-walkthroughid={
                                                    plans?.ufSetupFeeChannelsTd
                                                }
                                            />
                                        </Tooltip>
                                    </Td>
                                    <Td>
                                        <Tooltip
                                            label="Response Group Setup Fee"
                                            placement="top"
                                            hasArrow>
                                            <Input
                                                style={{
                                                    background: darkMode
                                                        ? '#181A20'
                                                        : 'white',
                                                    color: `${darkMode ? 'dark.textDark' : 'inherit'}`,
                                                    borderColor: `${darkMode ? 'dark.tcap-borderGray' : 'inherit'}`,
                                                }}
                                                type="number"
                                                step="any"
                                                value={values?.rgsSetupFee}
                                                onChange={(e) =>
                                                    setFieldValue(
                                                        'rgsSetupFee',
                                                        e.target.value,
                                                    )
                                                }
                                                data-walkthroughid={
                                                    plans.ufSetupFeeResponseGroupTd
                                                }
                                            />
                                        </Tooltip>
                                    </Td>
                                </Tr>
                                {/* Rate Row */}
                                <Tr>
                                    <Td fontWeight="bold">Rate</Td>
                                    <Td>
                                        <Tooltip
                                            label="User Rate"
                                            placement="top"
                                            hasArrow>
                                            <Input
                                                style={{
                                                    background: darkMode
                                                        ? '#181A20'
                                                        : 'white',
                                                    color: `${darkMode ? 'dark.textDark' : 'inherit'}`,
                                                    borderColor: `${darkMode ? 'dark.tcap-borderGray' : 'inherit'}`,
                                                }}
                                                type="number"
                                                step="any"
                                                value={values?.userRate}
                                                onChange={(e) =>
                                                    setFieldValue(
                                                        'userRate',
                                                        e.target.value,
                                                    )
                                                }
                                                data-walkthroughid={
                                                    plans?.ufRateUserTd
                                                }
                                            />
                                        </Tooltip>
                                    </Td>
                                    <Td>
                                        <Tooltip
                                            label="Trunk Rate"
                                            placement="top"
                                            hasArrow>
                                            <Input
                                                style={{
                                                    background: darkMode
                                                        ? '#181A20'
                                                        : 'white',
                                                    color: `${darkMode ? 'dark.textDark' : 'inherit'}`,
                                                    borderColor: `${darkMode ? 'dark.tcap-borderGray' : 'inherit'}`,
                                                }}
                                                type="number"
                                                step="any"
                                                value={values?.trunkRate}
                                                onChange={(e) =>
                                                    setFieldValue(
                                                        'trunkRate',
                                                        e.target.value,
                                                    )
                                                }
                                                data-walkthroughid={
                                                    plans.ufRateTrunkTd
                                                }
                                            />
                                        </Tooltip>
                                    </Td>
                                    <Td>
                                        <Tooltip
                                            label="Channels Rate"
                                            placement="top"
                                            hasArrow>
                                            <Input
                                                style={{
                                                    background: darkMode
                                                        ? '#181A20'
                                                        : 'white',
                                                    color: `${darkMode ? 'dark.textDark' : 'inherit'}`,
                                                    borderColor: `${darkMode ? 'dark.tcap-borderGray' : 'inherit'}`,
                                                }}
                                                type="number"
                                                step="any"
                                                value={values?.sipChannelRate}
                                                onChange={(e) =>
                                                    setFieldValue(
                                                        'sipChannelRate',
                                                        e.target.value,
                                                    )
                                                }
                                                data-walkthroughid={
                                                    plans.ufRateChannelsTd
                                                }
                                            />
                                        </Tooltip>
                                    </Td>
                                    <Td>
                                        <Tooltip
                                            label="Response Group Rate"
                                            placement="top"
                                            hasArrow>
                                            <Input
                                                style={{
                                                    background: darkMode
                                                        ? '#181A20'
                                                        : 'white',
                                                    color: `${darkMode ? 'dark.textDark' : 'inherit'}`,
                                                    borderColor: `${darkMode ? 'dark.tcap-borderGray' : 'inherit'}`,
                                                }}
                                                type="number"
                                                step="any"
                                                value={values?.rgsRate}
                                                onChange={(e) =>
                                                    setFieldValue(
                                                        'rgsRate',
                                                        e.target.value,
                                                    )
                                                }
                                                data-walkthroughid={
                                                    plans?.ufRateResponseGroupTd
                                                }
                                            />
                                        </Tooltip>
                                    </Td>
                                </Tr>
                                {/* Bill Row (Checkboxes, custom v4) */}
                                <Tr>
                                    <Td fontWeight="bold">Bill</Td>
                                    <Td textAlign="center">
                                        <Tooltip
                                            label="Bill User"
                                            placement="top"
                                            hasArrow>
                                            <Field
                                                component={BooleanToggle}
                                                id="userType"
                                                name="userType"
                                                isChecked={values.userType}
                                                onChange={(value) =>
                                                    setFieldValue(
                                                        'userType',
                                                        value,
                                                    )
                                                }
                                                walkthroughId={
                                                    plans.ufBillUserCheckbox
                                                }
                                            />
                                        </Tooltip>
                                    </Td>
                                    <Td textAlign="center">
                                        <Tooltip
                                            label="Bill Trunk"
                                            placement="top"
                                            hasArrow>
                                            <Field
                                                component={BooleanToggle}
                                                id="trunkType"
                                                name="trunkType"
                                                isChecked={values.trunkType}
                                                onChange={(value) =>
                                                    setFieldValue(
                                                        'trunkType',
                                                        value,
                                                    )
                                                }
                                                walkthroughId={
                                                    plans.ufBillTrunkCheckbox
                                                }
                                            />
                                        </Tooltip>
                                    </Td>
                                    <Td textAlign="center">
                                        <></>
                                    </Td>
                                    <Td textAlign="center">
                                        <Tooltip
                                            label="Bill Response Group"
                                            placement="top"
                                            hasArrow>
                                            <Field
                                                component={BooleanToggle}
                                                id="rgsType"
                                                name="rgsType"
                                                isChecked={values.rgsType}
                                                onChange={(value) =>
                                                    setFieldValue(
                                                        'rgsType',
                                                        value,
                                                    )
                                                }
                                                walkthroughId={
                                                    plans.ufBillResponseGroupCheckbox
                                                }
                                            />
                                        </Tooltip>
                                    </Td>
                                </Tr>
                            </Tbody>
                        </Table>
                    </Box>
                </Stack>
            )}
        </FormBase>
    );
};

export default PlanDetails;
